import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Field, NextImage, RichText, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useState } from 'react';
import Video from '../../../components/shared/Video/Video';
import {
  StAccordion,
  StAccordionDetails,
  StAccordionSummary,
  StImageMediaContainer,
  StVideoMediaContainer,
} from './Accordion.styles';
import UiText from '../Typography/UiText';
import UiHeading from '../Heading';
import UiBox from '../Box';
import Head from 'next/head';

type AccordionDetails = {
  fields: {
    accordionTitle: Field<string>;
    accordionDescription: Field<string>;
    accordionAdditionalDescription?: any;
    imageUrl?: any;
    videoUrl?: any;
  };
};

type AccordionProps = {
  expandOnlyOneAccordion: boolean;
  hideExpandAllButton: boolean;
  sectionsList: AccordionDetails[];
  expandAllText: string;
  collapseAllText: string;
  children: (
    singleLineHeading: boolean,
    handleExpand: () => any,
    showExpandIcon: boolean,
    showExpandIconText: string
  ) => JSX.Element;
  headingWithIconVariant?: string;
};

const Accordion = (props: AccordionProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const [expandAll, setExpandAll] = useState(
    new Array<boolean>(props.sectionsList.length).fill(false)
  );

  const singleLineHeading = props?.headingWithIconVariant === 'ImageUp' ? false : true;

  const [showExpandIcon, setShowExpandIcon] = useState(true);
  const [showExpandIconText, setShowExpandIconText] = useState(props.expandAllText);

  const generateVideoSchema = (accordionSection: any) => {
    const { videoUrl, videoName, videoDescription, videoThumbnailURL, videoUploadDate } =
      accordionSection?.fields;

    if (!videoUrl?.value?.href) {
      return null;
    }

    const videoSchema = {
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: videoName?.value,
      description: videoDescription?.value,
      thumbnailUrl: videoThumbnailURL?.value?.href,
      uploadDate: videoUploadDate?.value?.split('T')[0],
      contentUrl: videoUrl?.value?.href,
      embedUrl: videoUrl?.value?.href,
    };

    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(videoSchema),
          }}
        />
      </Head>
    );
  };

  const handleExpand = () => {
    if (expandAll.some((data) => data === false)) {
      setExpandAll((prevState) => {
        const expandedData = prevState.fill(true);
        return [...expandedData];
      });
    } else {
      setExpandAll((prevState) => {
        const expandedData = prevState.fill(false);
        return [...expandedData];
      });
    }
  };

  useEffect(() => {
    if (sitecoreContext?.pageEditing) {
      setExpandAll(new Array<boolean>(props.sectionsList.length).fill(true));
    } else {
      setExpandAll(new Array<boolean>(props.sectionsList.length).fill(false));
    }
  }, [props.sectionsList]);

  useEffect(() => {
    if (expandAll.some((data) => data === false)) {
      setShowExpandIcon(true);
      setShowExpandIconText(props.expandAllText);
    } else {
      setShowExpandIcon(false);
      setShowExpandIconText(props.collapseAllText);
    }
  }, [expandAll, props.collapseAllText, props.expandAllText]);

  function renderMediaItem(accordionSection: any) {
    const { videoUrl, imageUrl, autoPlayVideo, videoThumbnailURL, videoName, videoDescription } =
      accordionSection?.fields;

    if (
      videoUrl?.value?.href !== '' &&
      videoUrl?.value?.href !== undefined &&
      videoUrl?.value?.href !== null
    ) {
      const videoProps = {
        fields: {
          fields: {
            videoUrl: { value: videoUrl?.value?.href },
            autoPlayVideo,
            videoThumbnailURL,
            videoName,
            videoDescription,
          },
        },
      };

      return (
        <>
          {generateVideoSchema(accordionSection)}
          <StVideoMediaContainer data-testid="video-container">
            <Video {...videoProps} />
          </StVideoMediaContainer>
        </>
      );
    } else if (
      imageUrl?.value?.src !== '' &&
      imageUrl?.value?.src !== undefined &&
      imageUrl?.value?.src !== null
    ) {
      return (
        <StImageMediaContainer data-testid="image-container">
          <NextImage field={imageUrl} height={382} width={686} />
        </StImageMediaContainer>
      );
    } else {
      return null;
    }
  }

  return (
    <>
      {props.children(singleLineHeading, handleExpand, showExpandIcon, showExpandIconText)}
      {props?.sectionsList &&
        props?.sectionsList.map((accordionSection, index) => {
          const key = `${accordionSection?.fields?.accordionTitle?.value}-${index}`;
          return (
            <StAccordion
              key={key}
              disableGutters={true}
              expanded={expandAll[index] || false}
              onChange={() => {
                setExpandAll((prevState) => {
                  const currentState: boolean = prevState[index];
                  if (!props.expandOnlyOneAccordion) {
                    expandAll[index] = !currentState;
                  } else {
                    for (let i = 0; i < expandAll.length; i++) {
                      if (i === index) {
                        expandAll[i] = !currentState;
                      } else {
                        expandAll[i] = false;
                      }
                    }
                  }

                  return [...expandAll];
                });
              }}
            >
              <StAccordionSummary
                expandIcon={<ChevronRightIcon sx={{ height: '28px', width: '27px' }} />}
              >
                <UiBox className="ui-accordion-control">
                  <UiHeading size="h5" styles={{ color: 'inherit' }} id="ui-accordion-title">
                    {accordionSection?.fields?.accordionTitle}
                  </UiHeading>
                </UiBox>
              </StAccordionSummary>
              <StAccordionDetails>
                <UiText color="default">
                  <RichText
                    field={accordionSection?.fields?.accordionDescription}
                    style={{ marginBottom: '12px' }}
                  />
                  {renderMediaItem(accordionSection)}
                  {sitecoreContext?.pageEditing && (
                    <RichText field={accordionSection?.fields?.accordionAdditionalDescription} />
                  )}
                  {!sitecoreContext?.pageEditing &&
                    accordionSection?.fields?.accordionAdditionalDescription?.value !== '' && (
                      <RichText field={accordionSection?.fields?.accordionAdditionalDescription} />
                    )}
                </UiText>
              </StAccordionDetails>
            </StAccordion>
          );
        })}
    </>
  );
};

export default Accordion;
